/**
 * @fileoverview 乗下船表Excelの取込（配乗シミュレーション用）
 * @description 新様式Excel（各月シート下部の「■乗下船イベント」表）を読み取り、
 *              乗下船イベントとして一括登録する。イベント登録により発令予約が
 *              自動生成される（過去日は即時反映＝履歴の取込にも使える）。
 *              人物は「氏名（役職）」表記を氏名で照合（空白無視・姓のみ一意なら許容）。
 */
import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import * as XLSX from 'xlsx';
import * as api from '../services/api';
import { FileUp, X, CheckCircle2, AlertTriangle } from 'lucide-react';

/** 親からファイル選択を開くためのハンドル */
export interface BoardingExcelImportHandle { openPicker: () => void }

interface UserLite { id: string; user_name: string }
interface Vessel { id: string; vessel_name: string }

interface ParsedPerson { raw: string; name: string; position: string | null; user_id: string | null }
interface ParsedEvent {
  key: string;
  event_date: string; end_date: string | null;
  event_type: 'change' | 'berth' | 'other';
  port_name: string; berth_note: string; note: string;
  boards: ParsedPerson[]; disembarks: ParsedPerson[];
  selected: boolean;
  sheet: string;
}

const TYPE_MAP: Record<string, 'change' | 'berth' | 'other'> = {
  '交代': 'change', '交代（乗下船）': 'change', '乗下船': 'change',
  '仮バース・停泊': 'berth', '仮バース': 'berth', '停泊': 'berth',
};

function normName(s: string): string { return s.replace(/[\s　]/g, ''); }

/** 括弧の外の区切り文字だけで分割する。「山田（船長/安全管理者）」を2人に割らないため */
function splitPersonTokens(text: string): string[] {
  const out: string[] = [];
  let buf = '';
  let depth = 0;
  for (const ch of text) {
    if (ch === '（' || ch === '(') depth++;
    else if (ch === '）' || ch === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && /[、,／/]/.test(ch)) {
      if (buf.trim()) out.push(buf.trim());
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

/** "氏名（役職）、氏名（役職）" を分解してユーザー照合 */
function parsePersons(cell: unknown, users: UserLite[]): ParsedPerson[] {
  const text = String(cell ?? '').trim();
  if (!text) return [];
  return splitPersonTokens(text).map(tok => {
    const m = tok.match(/^(.*?)[（(]([^（）()]*)[）)]\s*$/);
    const name = (m ? m[1] : tok)!.trim();
    const position = m ? (m[2] ?? '').trim() || null : null;
    const nn = normName(name);
    let user = users.find(u => normName(u.user_name) === nn);
    if (!user) {
      // 姓のみ等の前方一致（1人に定まる場合のみ）
      const hits = users.filter(u => normName(u.user_name).startsWith(nn) && nn.length >= 2);
      if (hits.length === 1) user = hits[0];
    }
    return { raw: tok, name, position, user_id: user?.id ?? null };
  });
}

/** 日付セル（Date / 'YYYY-MM-DD' / 'M/D'）を YYYY-MM-DD へ。シートの年を補完 */
function parseDateCell(v: unknown, sheetYear: number): string | null {
  if (v == null || v === '') return null;
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[\/月](\d{1,2})日?$/);
  if (m) return `${sheetYear}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`;
  return null;
}

/** 表紙シートの「対象船舶」を読む（無ければ null） */
function parseCoverVessel(wb: XLSX.WorkBook): string | null {
  const ws = wb.Sheets['表紙'];
  if (!ws) return null;
  const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  for (const r of rows) {
    const i = r.findIndex(c => String(c ?? '').trim() === '対象船舶');
    if (i >= 0) {
      for (let j = i + 1; j < r.length; j++) {
        const v = String(r[j] ?? '').trim();
        if (v) return v;
      }
    }
  }
  return null;
}

function parseWorkbook(wb: XLSX.WorkBook, users: UserLite[]): { events: ParsedEvent[]; warnings: string[] } {
  const events: ParsedEvent[] = [];
  const warnings: string[] = [];
  let foundSection = false;

  for (const sheetName of wb.SheetNames) {
    const ym = sheetName.match(/^(\d{4})年(\d{1,2})月$/);
    if (!ym) continue;
    const year = Number(ym[1]);
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;
    const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

    const headIdx = rows.findIndex(r => r.some(c => String(c ?? '').includes('■乗下船イベント')));
    if (headIdx === -1) continue;
    foundSection = true;
    // 見出し行（日付/終了日/区分/...）はセクション行の次。列位置は見出し名で特定する
    // （新レイアウトは列結合で位置が変わるため。旧様式のA〜H配置もこのマッピングで読める）
    const headerRow = (rows[headIdx + 1] ?? []) as unknown[];
    const colIdx: Record<string, number> = {};
    headerRow.forEach((v, i) => {
      const t = String(v ?? '').trim();
      if (t && colIdx[t] === undefined) colIdx[t] = i;
    });
    const col = (name: string, fallback: number) => colIdx[name] ?? fallback;
    const cDate = col('日付', 0), cEnd = col('終了日', 1), cType = col('区分', 2), cPort = col('港', 3);
    const cBerth = col('バース・補足', 4), cBoard = col('乗船者（役職）', 5), cDis = col('下船者（役職）', 6), cNote = col('メモ', 7);
    let empty = 0;
    for (let ri = headIdx + 2; ri < rows.length && empty < 5; ri++) {
      const r = rows[ri] ?? [];
      const date = parseDateCell(r[cDate], year);
      if (!date) {
        if ((r as unknown[]).every(c => String(c ?? '').trim() === '')) empty++;
        continue;
      }
      empty = 0;
      const endDate = parseDateCell(r[cEnd], year);
      const typeRaw = String(r[cType] ?? '').trim();
      const boards = parsePersons(r[cBoard], users);
      const disembarks = parsePersons(r[cDis], users);
      const ev: ParsedEvent = {
        key: `${sheetName}-${ri}`,
        event_date: date, end_date: endDate,
        event_type: TYPE_MAP[typeRaw] ?? (boards.length || disembarks.length ? 'change' : 'other'),
        port_name: String(r[cPort] ?? '').trim(),
        berth_note: String(r[cBerth] ?? '').trim(),
        note: String(r[cNote] ?? '').trim(),
        boards, disembarks,
        selected: true,
        sheet: sheetName,
      };
      for (const p of [...boards, ...disembarks]) {
        if (!p.user_id) warnings.push(`${sheetName} ${date}: 「${p.raw}」に一致する乗組員が見つかりません（この人はスキップされます）`);
      }
      events.push(ev);
    }
  }
  if (!foundSection) {
    warnings.push('「■乗下船イベント」の表が見つかりませんでした。新様式のExcel（本システムからExcel出力したもの、またはお渡しした新様式ファイル）をご利用ください。');
  }
  events.sort((a, b) => a.event_date.localeCompare(b.event_date));
  return { events, warnings };
}

const BoardingExcelImport = forwardRef<BoardingExcelImportHandle, {
  vessels: Vessel[]; users: UserLite[]; onImported: () => void;
  /** trueで自前のボタンを描画しない（親のメニューから openPicker で起動） */
  hideTrigger?: boolean;
}>(({ vessels, users, onImported, hideTrigger }, ref) => {
  const fileRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => ({ openPicker: () => fileRef.current?.click() }));
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<ParsedEvent[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [vesselId, setVesselId] = useState('');
  const [notify, setNotify] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const onFile = async (f: File) => {
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const parsed = parseWorkbook(wb, users);
      setEvents(parsed.events);
      setWarnings(parsed.warnings);
      // 表紙の「対象船舶」があればそれを取込先に自動選択
      const coverName = parseCoverVessel(wb);
      const coverVessel = coverName ? vessels.find(v => v.vessel_name === coverName) : null;
      setVesselId(coverVessel?.id ?? vessels[0]?.id ?? '');
      setResult(null);
      setOpen(true);
    } catch {
      alert('Excelの読み取りに失敗しました。ファイル形式をご確認ください。');
    }
  };

  const doImport = async () => {
    const targets = events.filter(e => e.selected);
    if (!targets.length || !vesselId) return;
    setImporting(true);
    let ok = 0; const errs: string[] = [];
    for (const e of targets) {
      try {
        await api.post('/boarding-sheet/events', {
          vessel_id: vesselId,
          event_date: e.event_date, end_date: e.end_date,
          event_type: e.event_type, port_name: e.port_name, berth_note: e.berth_note, note: e.note,
          notify,
          items: [
            ...e.boards.filter(p => p.user_id).map(p => ({ user_id: p.user_id, action: 'board', position: p.position })),
            ...e.disembarks.filter(p => p.user_id).map(p => ({ user_id: p.user_id, action: 'disembark', position: p.position })),
          ],
        });
        ok++;
      } catch (er: any) { errs.push(`${e.event_date}: ${er?.message ?? '登録失敗'}`); }
    }
    setImporting(false);
    setResult(`イベント ${ok} 件を登録しました${errs.length ? `（失敗 ${errs.length} 件: ${errs.slice(0, 3).join(' / ')}）` : ''}`);
    onImported();
  };

  const today = new Date().toISOString().slice(0, 10);
  const chip = (p: ParsedPerson, kind: 'b' | 'd') => (
    <span key={p.raw + kind} style={{
      fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 7px', marginRight: 4, display: 'inline-block', marginBottom: 2,
      color: p.user_id ? (kind === 'b' ? '#166534' : '#B42318') : '#92600A',
      background: p.user_id ? (kind === 'b' ? '#E4F0E7' : '#FCE9E7') : '#FEF6E7',
      border: p.user_id ? 'none' : '1px dashed #E2C06E',
    }}>
      {kind === 'b' ? '▲' : '▼'}{p.name}{p.position ? `（${p.position}）` : ''}{!p.user_id && ' ?'}
    </span>
  );

  return (
    <>
      {!hideTrigger && (
        <button className="btn-o" onClick={() => fileRef.current?.click()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '7px 14px', fontWeight: 700 }}
          title="新様式の乗下船表Excelから乗下船イベントを取り込みます">
          <FileUp size={14} />Excel取込
        </button>
      )}
      <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,28,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 220 }} onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: 760, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '18px 22px 10px' }}>
              <span style={{ fontSize: 16.5, fontWeight: 800, color: '#16283B' }}>乗下船表Excelの取込プレビュー</span>
              <button onClick={() => setOpen(false)} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#8395A7', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ padding: '0 22px 6px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: '#5A6B7E' }}>取込先の船:</span>
              <select value={vesselId} onChange={e => setVesselId(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #DCE3EA', fontSize: 13, fontWeight: 600 }}>
                {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#2B3B4E', cursor: 'pointer' }}>
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                対象者へお知らせ配信（過去分の取込では通常オフ）
              </label>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8395A7' }}>{events.filter(e => e.selected).length} / {events.length} 件を取込</span>
            </div>

            {warnings.length > 0 && (
              <div style={{ margin: '4px 22px', background: '#FEF6E7', border: '1px solid #F2DCA6', borderRadius: 10, padding: '8px 12px', maxHeight: 96, overflowY: 'auto' }}>
                {warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 11.5, color: '#92600A', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />{w}
                  </div>
                ))}
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 22px 10px' }}>
              {events.map(e => (
                <label key={e.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 10px', borderRadius: 10, border: '1px solid #E8EDF2', marginBottom: 6, cursor: 'pointer', background: e.selected ? '#FCFDFE' : '#F7F8FA', opacity: e.selected ? 1 : 0.6 }}>
                  <input type="checkbox" checked={e.selected} style={{ marginTop: 3 }}
                    onChange={ev => setEvents(prev => prev.map(x => x.key === e.key ? { ...x, selected: ev.target.checked } : x))} />
                  <div style={{ minWidth: 88, fontSize: 12.5, fontWeight: 800, color: '#16283B' }}>
                    {e.event_date}
                    {e.event_date < today && <div style={{ fontSize: 10, color: '#92600A', fontWeight: 700 }}>過去日=即時反映</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#2B3B4E', marginBottom: 3 }}>
                      {e.port_name && <strong>⚓ {e.port_name}　</strong>}
                      {e.berth_note && <span style={{ color: '#8395A7' }}>{e.berth_note}　</span>}
                      {e.note && <span style={{ color: '#5A6B7E' }}>※{e.note}</span>}
                    </div>
                    <div>
                      {e.boards.map(p => chip(p, 'b'))}
                      {e.disembarks.map(p => chip(p, 'd'))}
                      {e.boards.length === 0 && e.disembarks.length === 0 && <span style={{ fontSize: 11, color: '#A6B0BC' }}>（人の乗下船なし・記録のみ）</span>}
                    </div>
                  </div>
                </label>
              ))}
              {events.length === 0 && <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: '#8395A7' }}>取り込めるイベントが見つかりませんでした</div>}
            </div>

            <div style={{ padding: '10px 22px 18px', display: 'flex', gap: 10, alignItems: 'center' }}>
              {result && <span style={{ fontSize: 12.5, fontWeight: 700, color: '#166534', display: 'flex', alignItems: 'center', gap: 5 }}><CheckCircle2 size={14} />{result}</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <button onClick={() => setOpen(false)} style={{ padding: '10px 18px', borderRadius: 10, border: '1.5px solid #DCE3EA', background: 'white', fontSize: 13, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer' }}>
                  {result ? '閉じる' : 'キャンセル'}
                </button>
                {!result && (
                  <button onClick={doImport} disabled={importing || !events.some(e => e.selected)}
                    style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: importing ? '#9DB8D2' : 'linear-gradient(135deg,#0E5AA7,#2EA8D5)', color: 'white', fontSize: 13.5, fontWeight: 800, cursor: importing ? 'not-allowed' : 'pointer' }}>
                    {importing ? '取込中…' : 'この内容で取り込む'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
BoardingExcelImport.displayName = 'BoardingExcelImport';

export default BoardingExcelImport;
